/* Maven v2 chrome — mobile nav toggle.

   Deliberately vanilla and self-contained. It must not touch, wrap or race
   window.Kumquat: the cart client binds its own selectors and this only owns
   the hamburger button and the <nav> it controls.

   The panel is shown by a [data-open] attribute rather than an inline style so
   the media query in maven-chrome.css stays the single source of truth for
   when a panel is even possible. Above the breakpoint the attribute is inert. */
(function () {
  'use strict';

  function setup(btn) {
    var navId = btn.getAttribute('aria-controls');
    var nav = navId && document.getElementById(navId);
    if (!nav) return;

    function close() {
      btn.setAttribute('aria-expanded', 'false');
      nav.removeAttribute('data-open');
    }

    function open() {
      btn.setAttribute('aria-expanded', 'true');
      nav.setAttribute('data-open', '');
    }

    btn.addEventListener('click', function () {
      if (btn.getAttribute('aria-expanded') === 'true') {
        close();
      } else {
        open();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (btn.getAttribute('aria-expanded') !== 'true') return;
      close();
      btn.focus();
    });

    // Following a link should not leave the panel open behind the next page.
    nav.addEventListener('click', function (e) {
      if (e.target.closest('a')) close();
    });

    // Resizing past the breakpoint must not strand an open panel.
    //
    // ⚠️ 1000px, and it MUST equal the header media query in maven-chrome.css.
    // This said 860px while the CSS said 1000px, which broke it both ways:
    // widening 800px → 900px fired the listener with matches=false and snapped
    // an open drawer shut while the header was still collapsed, and crossing
    // the real breakpoint at 1000px did not fire it at all, leaving data-open
    // and aria-expanded set on a nav whose hamburger had just disappeared.
    // There is no way to read a CSS breakpoint from here, so the two numbers
    // are kept in step by hand — change one, change the other.
    if (window.matchMedia) {
      var mq = window.matchMedia('(max-width: 1000px)');
      var onChange = function (ev) { if (!ev.matches) close(); };
      if (mq.addEventListener) {
        mq.addEventListener('change', onChange);
      } else if (mq.addListener) {
        mq.addListener(onChange);
      }
    }
  }

  /* The "Our Food" parent is a <button> since 2026-09-24 (Heidi: "just a drop
   * down and not clickable"). CSS opens the panel on :hover and
   * :focus-within, which covers a mouse and a keyboard with no script. It does
   * not cover a TAP: Safari on iOS does not focus a button it taps, so the
   * panel would never open on an iPad. This adds two states the CSS reads:
   *
   *   data-open    open, whatever the pointer or focus is doing (a tap)
   *   data-closed  shut, even while hovered or focused (Escape, a second click)
   *
   * ⚠️ `aria-expanded` IS READ OFF THE PANEL, NOT KEPT AS A FLAG. The first
   * version kept its own flag and set it only on a click, so a keyboard user
   * who tabbed onto the button opened the panel through :focus-within and
   * heard "collapsed" (review of #397). Three things can open the panel and
   * only one of them is this script, so after every event this asks the panel
   * whether it is showing and reports that.
   *
   * In the drawer (1000px and below) the panel is always visible as a nested
   * list: the button reports expanded and a tap changes nothing. */
  function setupParent(btn) {
    var group = btn.closest('.mv-nav__group');
    var panel = group && group.querySelector('.mv-nav__panel');
    if (!group || !panel) return;
    var drawer = window.matchMedia ? window.matchMedia('(max-width: 1000px)') : null;
    var inDrawer = function () { return !!(drawer && drawer.matches); };

    function report() {
      var shown = inDrawer() || getComputedStyle(panel).visibility === 'visible';
      btn.setAttribute('aria-expanded', shown ? 'true' : 'false');
    }
    function later() { window.requestAnimationFrame(report); }
    function reset() {
      group.removeAttribute('data-open');
      group.removeAttribute('data-closed');
      later();
    }

    /* ⚠️ A POINTER CLICK ON A HOVER-OPENED PANEL DOES NOTHING, and the first
     * version closed it. It read "the panel is showing" as "a click opened
     * it", so a mouse user who hovered and then clicked (the old link trained
     * exactly that) had the panel slam shut under a cursor that had not moved,
     * and stay shut until the pointer left and came back (review of #397).
     *
     * So the two inputs are told apart. A keyboard Enter or Space reaches here
     * as a click with `detail` 0, and it toggles: that is how a keyboard user
     * closes a panel that focus opened. A pointer click closes only a panel
     * this script opened (`data-open`), and opens one that is shut. */
    btn.addEventListener('click', function (e) {
      if (inDrawer()) return;
      var keyboard = e.detail === 0;
      var shown = getComputedStyle(panel).visibility === 'visible';
      if (group.hasAttribute('data-open') || (keyboard && shown)) {
        group.removeAttribute('data-open');
        group.setAttribute('data-closed', '');
      } else if (!shown) {
        group.removeAttribute('data-closed');
        group.setAttribute('data-open', '');
      }
      later();
    });
    group.addEventListener('mouseenter', function () { group.removeAttribute('data-closed'); later(); });
    group.addEventListener('mouseleave', function () {
      if (!group.contains(document.activeElement)) group.removeAttribute('data-closed');
      later();
    });
    group.addEventListener('focusin', function () { later(); });
    group.addEventListener('focusout', function (e) {
      if (!group.contains(e.relatedTarget)) reset();
    });
    document.addEventListener('click', function (e) {
      if (!group.contains(e.target)) reset();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || inDrawer()) return;
      if (getComputedStyle(panel).visibility !== 'visible') return;
      group.removeAttribute('data-open');
      group.setAttribute('data-closed', '');
      btn.focus();
      later();
    });
    if (drawer) {
      if (drawer.addEventListener) drawer.addEventListener('change', reset);
      else if (drawer.addListener) drawer.addListener(reset);
    }
    report();
  }

  function init() {
    var buttons = document.querySelectorAll('[data-maven-nav-toggle]');
    for (var i = 0; i < buttons.length; i++) setup(buttons[i]);
    var parents = document.querySelectorAll('[data-mv-nav-parent]');
    for (var j = 0; j < parents.length; j++) setupParent(parents[j]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* Email sign-ups, for the "Klaviyo Email Lead Capture" custom pixel.

   That pixel (Settings > Customer events) listens for the custom event
   `maven_email_signup` and sends a GA4 `generate_lead` event. The old live
   theme published the event from a script at the end of layout/theme.liquid.
   The Dawn theme did not carry that script, so from the launch on
   2026-09-30 until this change, GA4 recorded no sign-up leads. Found
   2026-10-01, when Heidi showed the pixel's code.

   The event names and the payload are the same as the old theme's. The DOM
   event `maven:email_signup` is kept too, because the old theme dispatched it. */
(function () {
  'use strict';

  window.addEventListener('klaviyoForms', function (e) {
    if (!e.detail || e.detail.type !== 'submit') return;
    var formId = e.detail.formId || 'klaviyo_form';
    document.dispatchEvent(new CustomEvent('maven:email_signup', {
      bubbles: true,
      detail: { formId: formId }
    }));
    if (window.Shopify && window.Shopify.analytics) {
      window.Shopify.analytics.publish('maven_email_signup', { formId: formId });
    }
  });
})();

/* Smile loads from its app embed again, since 2026-10-02. No code here.

   Heidi turned the embed off on 2026-10-01 for phone speed, and #537 then
   loaded Smile only for a referral visit. That broke points redemption: a
   member redeemed points for a free product, Smile took the points, and no
   product reached the cart, because Smile's script was not on the page.
   Smile's own email the same day said "Customers can't redeem points until
   you add Smile to your new theme". Heidi turned the embed back on that
   night, and this change removed the referral-only loader, so that Smile
   does not load twice.

   ⚠️ DO NOT LOAD SMILE FROM THEME CODE FOR SIGNED-IN MEMBERS. Smile needs a
   signed customer token to know whose points to use, and the signing key is
   a secret. Smile's guide says it "must be generated on the backend", and
   a secret never goes in a theme file. A members-only loader needs a
   server, such as a Kumquat App Proxy endpoint that returns the token. */

/* The "Larger text" switch, part 3 of 3: the click.

   snippets/maven-text-size.liquid explains the three parts. That snippet has
   already set the class from the saved choice before the first paint. This
   only keeps aria-pressed true to the class, and saves a new choice.

   ⚠️ THE KEY AND THE CLASS ARE ALSO WRITTEN IN snippets/maven-text-size.liquid.
   Change all three together.

   One listener on the document, not one per button, because the header
   renders two buttons: one in the strip and one in the drawer. */
(function () {
  'use strict';

  var KEY = 'mv-text-size';
  var CLASS = 'mv-text-large';
  var root = document.documentElement;

  function sync() {
    var on = root.classList.contains(CLASS) ? 'true' : 'false';
    var buttons = document.querySelectorAll('[data-mv-textsize]');
    for (var i = 0; i < buttons.length; i++) buttons[i].setAttribute('aria-pressed', on);
  }

  /* A storage failure (a private window, or blocked site data) still changes
     the page. The choice is then only lost at the next page. */
  function set(on) {
    root.classList.toggle(CLASS, on);
    try {
      if (on) localStorage.setItem(KEY, 'large');
      else localStorage.removeItem(KEY);
    } catch (e) {}
    sync();
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-mv-textsize]');
    if (!btn) return;
    set(!root.classList.contains(CLASS));
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }
})();
